import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Button,
  Container,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as Yup from "yup";
import Select from "react-select";
import { allowOnlyNumber, urlEndPoint } from "helpers/GeneralHelper";
import {
  addOnboarding,
  fetchOnboardingAccountExecutiveUsers,
  fetchOnboardingAdSpendOptions,
  fetchOnboardingClientPrefill,
  fetchOnboardingFormSchema,
  fetchOnboardingIndeedAccessOptions,
  fetchOnboardingSafetyOptions,
  getOnboardingDataById,
  mapOnboardingOptionsApiToSelectOptions,
  updateOnboarding,
} from "model/OnboardingModel";
import {
  getCommonJobCategoryOptions,
  getCommonPlanOptions,
  mapCommonPlanApiToSelectOptions,
} from "model/ClientRegisterModel";
import { getCompanyDataById, submitCompanyAutocomplete } from "model/CompanyModel";
import CompanyAddEditModal from "pages/AllCommonModal/CompanyAddEditModal/company-add-edit-modal";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const initialState = {
  id: "",
  onboardingType: "newclient",
  companyId: "",
  clientName: "",
  clientId: "",
  billingStartDate: "",
  billName: "",
  billEmail: "",
  planId: "",
  subscriptionAmount: 0,
  safetyId: "",
  jobCategoryId: "",
  indeedAccessId: "pending",
  adSpendBy: null,
  accountOwnerUserId: "",
  mecLabLocation: "",
  jotFormFile: null,
  jotFormFileId: 0,
  fadvLogin: "pending",
  fadvAccount: "",
  fadvUser: "",
  fadvPassword: "",
  onboardingNote: "",
  isApproved: false,
};

const clientTypeOptions = [
  { value: "newclient", label: "New Client" },
  { value: "restartclient", label: "Restart Client" },
];

const fadvLoginOptions = [
  { value: "pending", label: "pending" },
  { value: "Received", label: "Received" },
  { value: "N/A", label: "N/A" },
];

const DEFAULT_RESTART_CLIENT_BAG = {
  checkAccess: false,
  reportTypeId: null,
  checkActive: 0,
  nameContains: null,
  useCallBagRules: false,
  useOnboardingBagRules: true
};

const normalizeLookup = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const toNullableSelectValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized === "" ? null : normalized;
};

const getMappedOptionValue = (options = [], rawValue = "") => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return "";
  const exact = options.find((opt) => String(opt?.value) === String(rawValue));
  if (exact) return exact.value;

  const normalizedRaw = normalizeLookup(rawValue);
  const byNormalizedValue = options.find((opt) => normalizeLookup(opt?.value) === normalizedRaw);
  if (byNormalizedValue) return byNormalizedValue.value;

  const byNormalizedLabel = options.find((opt) => normalizeLookup(opt?.label) === normalizedRaw);
  if (byNormalizedLabel) return byNormalizedLabel.value;

  return String(rawValue);
};

const normalizeOnboardingType = (row = {}) => {
  const rawType = row?.onboardingType || row?.clientType || row?.type || "";
  const normalizedType = normalizeLookup(rawType);
  if (normalizedType === "newclient" || normalizedType === "new") return "newclient";
  if (normalizedType === "restartclient" || normalizedType === "restart" || normalizedType === "existingclient") {
    return "restartclient";
  }
  return row?.clientId ? "restartclient" : "newclient";
};

const normalizeAdSpendByValue = (rawValue = "") => {
  const normalized = normalizeLookup(rawValue);
  if (!normalized) return "";
  if (normalized.includes("ancileservices") || normalized === "adspendancileservices") {
    return "Ad Spend by Ancile Services";
  }
  if (normalized.includes("byclient") || normalized === "client") {
    return "Ad Spend by Client";
  }
  if (normalized === "na") {
    return "N/A";
  }
  return rawValue;
};

const formatDateForInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const parsedDate = new Date(s);
  if (Number.isNaN(parsedDate.getTime())) return "";
  const y = parsedDate.getFullYear();
  const m = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const d = String(parsedDate.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getAllTouchedFields = () => ({
  onboardingType: true,
  clientName: true,
  clientId: true,
  companyId: true,
  billingStartDate: true,
  billName: true,
  billEmail: true,
  planId: true,
  jobCategoryId: true,
  accountOwnerUserId: true,
  subscriptionAmount: true,
  fadvLogin: true,
  fadvAccount: true,
  fadvUser: true,
  fadvPassword: true,
  onboardingNote: true,
});

const isFadvLoginReceived = (value = "") =>
  String(value || "").trim().toLowerCase() === "received";

const onboardingSchema = Yup.object().shape({
  onboardingType: Yup.string().required("Client type is required"),
  clientName: Yup.string().when("onboardingType", {
    is: "newclient",
    then: (schema) => schema.trim().required("Client name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  clientId: Yup.string().when("onboardingType", {
    is: "restartclient",
    then: (schema) => schema.trim().required("Client is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  companyId: Yup.string().trim().required("Company is required"),
  billingStartDate: Yup.string().required("Billing date is required"),
  billName: Yup.string().trim().required("Billing name is required"),
  billEmail: Yup.string().trim().email("Enter a valid billing email").required("Billing email is required"),
  planId: Yup.string().notRequired(),
  subscriptionAmount: Yup.mixed().notRequired(),
  safetyId: Yup.string().notRequired(),
  jobCategoryId: Yup.string().required("Job category is required"),
  indeedAccessId: Yup.string().notRequired(),
  adSpendBy: Yup.string().notRequired(),
  accountOwnerUserId: Yup.string().when("onboardingType", {
    is: (type) => type === "newclient" || type === "restartclient",
    then: (schema) => schema.trim().required("Account owner is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mecLabLocation: Yup.string().trim().notRequired(),
  jotFormFile: Yup.mixed().notRequired(),
  fadvLogin: Yup.string().notRequired(),
  fadvAccount: Yup.string().when("fadvLogin", {
    is: isFadvLoginReceived,
    then: (schema) => schema.trim().required("FADV account is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  fadvUser: Yup.string().when("fadvLogin", {
    is: isFadvLoginReceived,
    then: (schema) => schema.trim().required("FADV user is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  fadvPassword: Yup.string().when("fadvLogin", {
    is: isFadvLoginReceived,
    then: (schema) => schema.trim().required("FADV password is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  onboardingNote: Yup.string().trim().notRequired(),
});

const AddEditOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissionData } = usePermissionContext() || {};
  const canCreateCompany = useMemo(
    () => Boolean(permissionData?.ClientCompanyAddAccess),
    [permissionData]
  );
  const query = new URLSearchParams(location.search || "");
  const editId = query.get("id") || location?.state?.itemId || null;
  const isViewMode = Boolean(location?.state?.viewMode);
  const isEditMode = Boolean(editId);

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loadingPage, setLoadingPage] = useState(!!isEditMode);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [jobCategoryOptions, setJobCategoryOptions] = useState([]);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [safetyOptions, setSafetyOptions] = useState([]);
  const [indeedAccessOptions, setIndeedAccessOptions] = useState([]);
  const [adSpendOptions, setAdSpendOptions] = useState([]);
  const [companyAddEditModalOpen, setCompanyAddEditModalOpen] = useState(false);
  const [restartClientBag, setRestartClientBag] = useState(DEFAULT_RESTART_CLIENT_BAG);
  const [showApproveCheckbox, setShowApproveCheckbox] = useState(false);

  const getSelectedOption = (options, value) =>
    options.find((opt) => String(opt.value) === String(value)) || null;

  const parseBooleanValue = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalizedValue = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "active"].includes(normalizedValue)) return true;
      if (["false", "0", "no", "n", "deactive", "inactive"].includes(normalizedValue)) return false;
    }
    return fallback;
  };

  const validateSingleField = async (name, value, payload = form) => {
    if (!onboardingSchema.fields[name]) return;
    try {
      await Yup.reach(onboardingSchema, name).validate(value, { context: payload, parent: payload });
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleInputChange = async (e) => {
    if (isViewMode) return;
    const { name, value, files, type, checked } = e.target;
    let fieldValue = files ? files[0] : type === "checkbox" ? checked : value;
    if (name === "subscriptionAmount") {
      fieldValue = allowOnlyNumber(value);
    }
    const next = { ...form, [name]: fieldValue };

    if (name === "onboardingType" && value === "newclient") {
      next.clientId = "";
    }
    if (name === "onboardingType" && value === "restartclient") {
      next.clientName = "";
      next.accountOwnerUserId = "";
    }

    setForm(next);
    if (touched[name]) {
      await validateSingleField(name, fieldValue, next);
    }
    if (name === "onboardingType") {
      await validateSingleField("clientName", next.clientName, next);
      await validateSingleField("clientId", next.clientId, next);
      await validateSingleField("accountOwnerUserId", next.accountOwnerUserId, next);
    }
    if (name === "fadvLogin") {
      if (!isFadvLoginReceived(fieldValue)) {
        next.fadvAccount = "";
        next.fadvUser = "";
        next.fadvPassword = "";
      }
      await validateSingleField("fadvAccount", next.fadvAccount, next);
      await validateSingleField("fadvUser", next.fadvUser, next);
      await validateSingleField("fadvPassword", next.fadvPassword, next);
    }
  };

  const handleBlur = async (e) => {
    if (isViewMode) return;
    const { name, value } = e.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    await validateSingleField(name, value, form);
  };

  const handleSelectChange = async (name, option) => {
    if (isViewMode) return;
    const value = option?.value ?? "";
    const next = { ...form, [name]: value };
    if (name === "onboardingType" && value === "newclient") {
      next.clientId = "";
    }
    if (name === "onboardingType" && value === "restartclient") {
      next.clientName = "";
      next.accountOwnerUserId = "";
    }
    setForm(next);
    setTouched((prev) => ({ ...prev, [name]: true }));
    await validateSingleField(name, value, next);
    if (name === "onboardingType") {
      await validateSingleField("clientName", next.clientName, next);
      await validateSingleField("clientId", next.clientId, next);
      await validateSingleField("accountOwnerUserId", next.accountOwnerUserId, next);
    }
    if (name === "fadvLogin") {
      if (!isFadvLoginReceived(value)) {
        next.fadvAccount = "";
        next.fadvUser = "";
        next.fadvPassword = "";
      }
      await validateSingleField("fadvAccount", next.fadvAccount, next);
      await validateSingleField("fadvUser", next.fadvUser, next);
      await validateSingleField("fadvPassword", next.fadvPassword, next);
    }
  };

  const fetchInitialOptions = async () => {
    const [
      jobCategoryRes,
      accountExecutiveUsersRes,
      planRes,
      adSpendRes,
      indeedAccessRes,
      safetyRes,
    ] = await Promise.all([
      getCommonJobCategoryOptions(),
      fetchOnboardingAccountExecutiveUsers(),
      getCommonPlanOptions(),
      fetchOnboardingAdSpendOptions(),
      fetchOnboardingIndeedAccessOptions(),
      fetchOnboardingSafetyOptions(),
    ]);

    setJobCategoryOptions(mapCommonPlanApiToSelectOptions(jobCategoryRes));

    setAccountOwnerOptions(mapOnboardingOptionsApiToSelectOptions(accountExecutiveUsersRes));

    const nextPlanOptions = mapCommonPlanApiToSelectOptions(planRes);
    const nextAdSpendOptions = mapOnboardingOptionsApiToSelectOptions(adSpendRes);
    const nextIndeedAccessOptions = mapOnboardingOptionsApiToSelectOptions(indeedAccessRes);
    const nextSafetyOptions = mapOnboardingOptionsApiToSelectOptions(safetyRes);

    setPlanOptions(nextPlanOptions);
    setAdSpendOptions(nextAdSpendOptions);
    setIndeedAccessOptions(nextIndeedAccessOptions);
    setSafetyOptions(nextSafetyOptions);

    return {
      planOptions: nextPlanOptions,
      adSpendOptions: nextAdSpendOptions,
      indeedAccessOptions: nextIndeedAccessOptions,
      safetyOptions: nextSafetyOptions,
    };
  };

  const fetchCompanyOptions = async (searchText = "") => {
    const response = await submitCompanyAutocomplete({
      nameContains: searchText || "",
    });
    const records = Array.isArray(response?.data)
      ? response.data
      : response?.data?.records || [];
    const options = records
      .map((item) => ({
        value: item?.id || item?._id || "",
        label: item?.display || item?.name || item?.title || item?.company || "Company",
      }))
      .filter((item) => item?.value);
    setCompanyOptions(options);
  };

  const ensureCompanyOptionById = async (companyIdValue = "") => {
    if (!companyIdValue) return;
    const hasOption = (companyOptions || []).some(
      (opt) => String(opt?.value) === String(companyIdValue)
    );
    if (hasOption) return;

    const response = await getCompanyDataById({ Id: companyIdValue });
    const row = response?.data;
    if (!row) return;
    const option = {
      value: row?.id || row?._id || companyIdValue,
      label: row?.display || row?.name || row?.title || row?.company || companyIdValue,
    };
    setCompanyOptions((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const alreadyExists = existing.some(
        (opt) => String(opt?.value) === String(option.value)
      );
      if (alreadyExists) return existing;
      return [...existing, option];
    });
  };

  const handleCompanyListRefresh = async () => {
    await fetchCompanyOptions("");
  };

  const normalizeRestartClientBag = (bag = {}) => {
    const { search, nameContains, ...rest } = bag;
    return {
      checkAccess: rest.checkAccess ?? false,
      reportTypeId: rest.reportTypeId ?? null,
      checkActive: rest.checkActive ?? 0,
      nameContains: nameContains ?? search ?? null,
      useCallBagRules: rest.useCallBagRules ?? false,
      useOnboardingBagRules: rest.useOnboardingBagRules ?? false,
    };
  };

  const loadFormSchema = async (optionLists = {}) => {
    const response = await fetchOnboardingFormSchema();
    const data = response?.data;
    if (!data) return;

    if (data?.restartClientBag && typeof data.restartClientBag === "object") {
      setRestartClientBag(normalizeRestartClientBag(data.restartClientBag));
    }

    setShowApproveCheckbox(parseBooleanValue(data?.showApproveCheckbox, false));

    const indeedOpts = optionLists.indeedAccessOptions || indeedAccessOptions;
    const defaults = data?.defaults || {};
    setForm((prev) => ({
      ...prev,
      billingStartDate:
        formatDateForInput(defaults.billingStartDate) || prev.billingStartDate,
      indeedAccessId: getMappedOptionValue(
        indeedOpts,
        defaults.indeedAccessId || "pending"
      ),
      fadvLogin: getMappedOptionValue(fadvLoginOptions, defaults.fadvLogin || "pending"),
      isApproved: parseBooleanValue(
        defaults.isApproved ?? defaults.IsApproved ?? data?.isApproved ?? data?.IsApproved,
        prev.isApproved
      ),
    }));
  };

  const applyClientPrefill = async (prefillData = {}) => {
    const companyId = prefillData?.companyId || "";
    const companyName = prefillData?.companyName || "";

    setForm((prev) => ({
      ...prev,
      companyId: companyId || prev.companyId,
      billName: prefillData?.billName || prev.billName,
      billEmail: prefillData?.billEmail || prev.billEmail,
      jobCategoryId: prefillData?.jobCategoryId || prev.jobCategoryId,
      accountOwnerUserId: prefillData?.accountOwnerUserId || prev.accountOwnerUserId,
    }));

    if (companyId) {
      if (companyName) {
        setCompanyOptions((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const hasOption = existing.some(
            (opt) => String(opt?.value) === String(companyId)
          );
          if (hasOption) return existing;
          return [...existing, { value: companyId, label: companyName }];
        });
      } else {
        await ensureCompanyOptionById(companyId);
      }
    }
  };

  const handleRestartClientSelect = async (option) => {
    if (isViewMode) return;
    const value = option?.value ?? "";
    const clientName = option?.label ?? "";
    const next = { ...form, clientId: value, clientName };
    setForm(next);
    setTouched((prev) => ({ ...prev, clientId: true }));
    await validateSingleField("clientId", value, next);

    if (!value) return;

    const response = await fetchOnboardingClientPrefill(value);
    const prefillData = response?.data;
    if (prefillData) {
      await applyClientPrefill(prefillData);
    }
  };

  const fetchById = async (id, optionLists = {}) => {
    const response = await getOnboardingDataById({ Id: id });
    const row = response?.data;
    if (!row) return;

    const planOpts = optionLists.planOptions || planOptions;
    const safetyOpts = optionLists.safetyOptions || safetyOptions;
    const indeedOpts = optionLists.indeedAccessOptions || indeedAccessOptions;
    const adSpendOpts = optionLists.adSpendOptions || adSpendOptions;

    setShowApproveCheckbox(
      parseBooleanValue(row?.showApproveCheckbox ?? row?.ShowApproveCheckbox, false)
    );

    setForm({
      ...initialState,
      id: row?.id || row?._id || "",
      onboardingType: normalizeOnboardingType(row),
      companyId: row?.companyId || row?.company || row?.companyName || "",
      clientName:
        row?.clientName ||
        row?.ClientName ||
        row?.client?.clientName ||
        row?.client?.name ||
        (typeof row?.client === "string" ? row.client : "") ||
        row?.Client ||
        "",
      clientId:
        row?.clientId ||
        row?.ClientId ||
        row?.client?.id ||
        row?.client?._id ||
        "",
      billingStartDate: formatDateForInput(
        row?.billingStartDate ??
        row?.BillingStartDate ??
        row?.billingDate ??
        row?.billDate ??
        ""
      ),
      billName: row?.billName || row?.billingName || "",
      billEmail: row?.billEmail || row?.billingEmail || "",
      planId: getMappedOptionValue(planOpts, row?.planId || row?.plan || ""),
      subscriptionAmount: row?.subscriptionAmount || "",
      safetyId: getMappedOptionValue(safetyOpts, row?.safetyId || row?.safety || ""),
      jobCategoryId: row?.jobCategoryId || row?.jobCategory || "",
      indeedAccessId: getMappedOptionValue(
        indeedOpts,
        row?.indeedAccessId || row?.indeedAccess || "pending"
      ),
      adSpendBy: getMappedOptionValue(
        adSpendOpts,
        row?.adSpendBy || row?.adSpend || row?.addSpend || row?.add_spend || null
      ),
      accountOwnerUserId:
        row?.accountOwnerUserId ||
        row?.accountOwnerId ||
        row?.account_owner_id ||
        row?.accountOwner?.id ||
        row?.accountOwner ||
        row?.accountOwnerName ||
        "",
      mecLabLocation: row?.mecLabLocation || "",
      jotFormFile: row?.jotFormFile || row?.jotFormFileUrl || "",
      jotFormFileId: row?.jotFormFileId || 0,
      fadvLogin: getMappedOptionValue(fadvLoginOptions, row?.fadvLogin || "pending"),
      fadvAccount: row?.fadvAccount || row?.account || "",
      fadvUser: row?.fadvUser || row?.user || "",
      fadvPassword: row?.fadvPassword || row?.password || "",
      onboardingNote: row?.onboardingNote || row?.note || "",
      isApproved: parseBooleanValue(row?.isApproved ?? row?.IsApproved, false),
    });
    const selectedCompanyId = row?.companyId || row?.company || row?.companyName || "";
    if (selectedCompanyId) {
      setCompanyOptions((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const hasOption = existing.some((opt) => String(opt?.value) === String(selectedCompanyId));
        if (hasOption) return existing;
        return [
          ...existing,
          {
            value: selectedCompanyId,
            label: row?.companyName || row?.company || selectedCompanyId,
          },
        ];
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initForm = async () => {
      setLoadingOptions(true);
      if (isEditMode) {
        setLoadingPage(true);
      }
      try {
        const optionLists = await fetchInitialOptions();
        if (cancelled) return;
        if (isEditMode) {
          await fetchById(editId, optionLists);
        } else {
          await loadFormSchema(optionLists);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.message || "Unable to load onboarding form");
        }
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
          setLoadingPage(false);
        }
      }
    };

    initForm();
    return () => {
      cancelled = true;
    };
  }, [editId, isEditMode]);

  useEffect(() => {
    if (form?.companyId) {
      ensureCompanyOptionById(form.companyId);
    }
  }, [form?.companyId, companyOptions]);

  // Keep accountOwnerId value aligned with select option values (same behavior as other id fields).
  useEffect(() => {
    if (!form?.accountOwnerUserId || !accountOwnerOptions?.length) return;
    const hasValueMatch = accountOwnerOptions.some(
      (opt) => String(opt?.value) === String(form.accountOwnerUserId)
    );
    if (hasValueMatch) return;
    const matchedByLabel = accountOwnerOptions.find(
      (opt) =>
        String(opt?.label || "").toLowerCase().trim() ===
        String(form.accountOwnerUserId || "").toLowerCase().trim()
    );
    if (matchedByLabel?.value) {
      setForm((prev) => ({ ...prev, accountOwnerUserId: matchedByLabel.value }));
    }
  }, [accountOwnerOptions, form?.accountOwnerUserId]);

  useEffect(() => {
    if (!form?.planId || !planOptions?.length) return;
    const mapped = getMappedOptionValue(planOptions, form.planId);
    if (mapped && mapped !== form.planId) {
      setForm((prev) => ({ ...prev, planId: mapped }));
    }
  }, [planOptions, form?.planId]);

  const toIsoDateTimeOrEmpty = (value) => {
    if (!value) return "";
    if (String(value).includes("T")) return value;
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return "";
    return parsedDate.toISOString();
  };

  const appendFormDataValue = (formData, key, value) => {
    if (value === undefined || value === null) {
      formData.append(key, "");
      return;
    }
    formData.append(key, value);
  };

  const buildPayload = () => {
    const payload = new FormData();
    const idValue = form.id || editId || "";
    const isNewClient = form.onboardingType === "newclient";
    const isRestartClient = form.onboardingType === "restartclient";

    appendFormDataValue(payload, "Id", idValue);
    appendFormDataValue(payload, "OnboardingType", form.onboardingType || "");
    appendFormDataValue(payload, "ClientId", isRestartClient ? (form.clientId || "") : "");
    appendFormDataValue(payload, "ClientName", isNewClient ? (form.clientName || "") : "");
    appendFormDataValue(payload, "CompanyId", form.companyId || "");
    appendFormDataValue(payload, "BillingStartDate", toIsoDateTimeOrEmpty(form.billingStartDate));
    appendFormDataValue(payload, "BillName", form.billName || "");
    appendFormDataValue(payload, "BillEmail", form.billEmail || "");
    appendFormDataValue(payload, "PlanId", form.planId || "");
    appendFormDataValue(payload, "SubscriptionAmount", Number(form.subscriptionAmount || 0));
    appendFormDataValue(payload, "SafetyId", form.safetyId || "");
    appendFormDataValue(payload, "JobCategoryId", form.jobCategoryId || "");
    appendFormDataValue(payload, "IndeedAccessId", form.indeedAccessId || "");
    appendFormDataValue(payload, "AdSpendBy", form.adSpendBy || null);
    appendFormDataValue(payload, "MECLabLocation", form.mecLabLocation || "");
    appendFormDataValue(payload, "AccountOwnerUserId", form.accountOwnerUserId || "");
    if (form.jotFormFile instanceof File) {
      payload.append("JotFormFile", form.jotFormFile);
    } else {
      appendFormDataValue(payload, "JotFormFile", "");
    }
    appendFormDataValue(payload, "JotFormFileId", Number(form.jotFormFileId || 0));
    appendFormDataValue(payload, "FADVLogin", form.fadvLogin || "");
    appendFormDataValue(payload, "FADVAccount", form.fadvAccount || "");
    appendFormDataValue(payload, "FADVUser", form.fadvUser || "");
    appendFormDataValue(payload, "FADVPassword", form.fadvPassword || "");
    appendFormDataValue(payload, "OnboardingNote", form.onboardingNote || "");
    if (showApproveCheckbox) {
      appendFormDataValue(payload, "isApproved", Boolean(form.isApproved));
    }
    return payload;
  };

  const validateOnboardingForm = async () => {
    try {
      await onboardingSchema.validate(form, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      (validationError?.inner || []).forEach((item) => {
        if (item?.path && !nextErrors[item.path]) {
          nextErrors[item.path] = item.message;
        }
      });
      setErrors(nextErrors);
      setTouched(getAllTouchedFields());
      return false;
    }
  };

  const handleSaveOnboarding = async () => {
    setSaving(true);
    const payload = buildPayload();
    const response = isEditMode
      ? await updateOnboarding(editId, payload)
      : await addOnboarding(payload);

    if (response?.success || response?.status) {
      toast.success(response?.message || `Onboarding ${isEditMode ? "updated" : "created"} successfully`);
      navigate(`/${urlEndPoint.onboardingList}`);
    } else {
      // toast.error(response?.message || "Unable to save onboarding");
      if (response?.error && typeof response.error === "object") {
        setErrors((prev) => ({ ...prev, ...response.error }));
        setTouched(getAllTouchedFields());
      }
    }
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode) return;
    try {
      const isFormValid = await validateOnboardingForm();
      if (!isFormValid) return;

      const swalResult = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to submit your request?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (!swalResult?.isConfirmed) return;

      await handleSaveOnboarding();
    } catch (error) {
      toast.error(error?.message || "Unable to save onboarding");
      return;
    }
  };

  const showFieldError = (field) => touched[field] && errors[field];
  const isNewClient = form.onboardingType === "newclient";
  const isRestartClient = form.onboardingType === "restartclient";
  const isFadvReceived = isFadvLoginReceived(form.fadvLogin);
  const hasFadvData = Boolean(
    String(form.fadvAccount || "").trim() ||
    String(form.fadvUser || "").trim() ||
    String(form.fadvPassword || "").trim()
  );
  const showFadvDetails = isFadvReceived || hasFadvData;
  const showLoader = loadingOptions || (isEditMode && loadingPage);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">
                  {isViewMode ? "View" : isEditMode ? "Edit" : "Create"} Onboarding
                </h5>
                <Button color="light" onClick={() => navigate(`/${urlEndPoint.onboardingList}`)}>
                  Back
                </Button>
              </div>
              <hr />

              {showLoader ? (
                <Loaders />
              ) : (
              <Form onSubmit={handleSubmit}>
                <fieldset
                  disabled={isViewMode}
                  style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
                >
                <Row>
                  <h5 className="mt-3 mb-3">Basic Information</h5>
                  <hr />
                  <Col md={4}>
                    <FormGroup>
                      <Label>Client Selection <span className="text-danger">*</span></Label>
                      <Select
                        options={clientTypeOptions}
                        className="select2-selection"
                        placeholder="Select Client Type"
                        value={getSelectedOption(clientTypeOptions, form.onboardingType)}
                        onChange={(option) => handleSelectChange("onboardingType", option)}
                        isDisabled={isViewMode || isEditMode}
                      />
                      {showFieldError("onboardingType") && <small className="text-danger">{errors.onboardingType}</small>}
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>
                        {isNewClient ? "Client Name" : "Client"}
                        <span className="text-danger"> *</span>
                      </Label>
                      {isNewClient ? (
                        <Input
                          name="clientName"
                          value={form.clientName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="Enter client name"
                        />
                      ) : isRestartClient ? (
                        <AsyncClientSelect
                          key={`onboarding-client-${String(form.clientId)}-${String(editId || "")}`}
                          pageName="OnboardingAdd"
                          value={form.clientId}
                          restartClientBag={restartClientBag}
                          isDisabled={isViewMode}
                          onChange={handleRestartClientSelect}
                          initialOption={
                            form.clientId
                              ? {
                                value: form.clientId,
                                label:
                                  (form.clientName && String(form.clientName).trim()) ||
                                  String(form.clientId),
                              }
                              : null
                          }
                          placeholder="Type at least 4 characters to search"
                        />
                      ) : null}
                      {isNewClient && showFieldError("clientName") && (
                        <small className="text-danger">{errors.clientName}</small>
                      )}
                      {isRestartClient && showFieldError("clientId") && (
                        <small className="text-danger">{errors.clientId}</small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Company <span className="text-danger">*</span></Label>
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                          <Select
                            options={companyOptions}
                            className="select2-selection"
                            placeholder="Select Company"
                            value={getSelectedOption(companyOptions, form.companyId)}
                            onChange={(option) => handleSelectChange("companyId", option)}
                            isDisabled={isViewMode}
                            onInputChange={(inputValue, { action }) => {
                              if (action === "input-change") {
                                const searchText = inputValue || "";
                                if (searchText.trim().length > 0) {
                                  fetchCompanyOptions(searchText);
                                } else {
                                  setCompanyOptions([]);
                                }
                              }
                            }}
                            isSearchable
                            isClearable
                            noOptionsMessage={() => "No company found"}
                          />
                        </div>
                        {!isViewMode && canCreateCompany ? (
                        <Button
                          type="button"
                          color="primary"
                          className="d-flex align-items-center justify-content-center px-0"
                          style={{ width: "38px", height: "38px", lineHeight: 1, marginTop: "1px" }}
                          onClick={() => setCompanyAddEditModalOpen(true)}
                        >
                          +
                        </Button>
                        ) : null}
                      </div>
                      {showFieldError("companyId") && <small className="text-danger">{errors.companyId}</small>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Billing Date <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        name="billingStartDate"
                        value={form.billingStartDate}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {showFieldError("billingStartDate") && <small className="text-danger">{errors.billingStartDate}</small>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Billing Name <span className="text-danger">*</span></Label>
                      <Input
                        name="billName"
                        value={form.billName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {showFieldError("billName") && <small className="text-danger">{errors.billName}</small>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Billing Email <span className="text-danger">*</span></Label>
                      <Input
                        type="email"
                        name="billEmail"
                        value={form.billEmail}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {showFieldError("billEmail") && <small className="text-danger">{errors.billEmail}</small>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Plan</Label>
                      <Select
                        options={planOptions}
                        className="select2-selection"
                        placeholder="Select Plan"
                        value={getSelectedOption(planOptions, form.planId)}
                        onChange={(option) => handleSelectChange("planId", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("planId") && <small className="text-danger">{errors.planId}</small>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Subscription Amount</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        name="subscriptionAmount"
                        value={form.subscriptionAmount}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter subscription amount"
                      />
                      {showFieldError("subscriptionAmount") && (
                        <small className="text-danger">{errors.subscriptionAmount}</small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Safety</Label>
                      <Select
                        options={safetyOptions}
                        className="select2-selection"
                        placeholder="Safety"
                        value={getSelectedOption(safetyOptions, form.safetyId)}
                        onChange={(option) => handleSelectChange("safetyId", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("safetyId") && <small className="text-danger">{errors.safetyId}</small>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Job Category <span className="text-danger">*</span></Label>
                      <Select
                        options={jobCategoryOptions}
                        className="select2-selection"
                        placeholder="Select Job Category"
                        value={getSelectedOption(jobCategoryOptions, form.jobCategoryId)}
                        onChange={(option) => handleSelectChange("jobCategoryId", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("jobCategoryId") && <small className="text-danger">{errors.jobCategoryId}</small>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Indeed Access</Label>
                      <Select
                        options={indeedAccessOptions}
                        className="select2-selection"
                        placeholder="Indeed Access"
                        value={getSelectedOption(indeedAccessOptions, form.indeedAccessId)}
                        onChange={(option) => handleSelectChange("indeedAccessId", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("indeedAccessId") && <small className="text-danger">{errors.indeedAccessId}</small>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Ad Spend By</Label>
                      <Select
                        options={adSpendOptions}
                        className="select2-selection"
                        placeholder="Ad Spend By"
                        value={getSelectedOption(adSpendOptions, form.adSpendBy)}
                        onChange={(option) => handleSelectChange("adSpendBy", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("adSpendBy") && <small className="text-danger">{errors.adSpendBy}</small>}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  {(isNewClient || isRestartClient) ? (
                    <Col md={4}>
                      <FormGroup>
                        <Label>
                          Account Owner
                          <span className="text-danger"> *</span>
                        </Label>
                        <Select
                          options={accountOwnerOptions}
                          className="select2-selection"
                          placeholder="Select Account Owner"
                          value={getSelectedOption(accountOwnerOptions, form.accountOwnerUserId)}
                          onChange={(option) => handleSelectChange("accountOwnerUserId", option)}
                          isDisabled={isViewMode}
                        />
                        {showFieldError("accountOwnerUserId") && (
                          <small className="text-danger">{errors.accountOwnerUserId}</small>
                        )}
                      </FormGroup>
                    </Col>
                  ) : null}
                  <Col md={4}>
                    <FormGroup>
                      <Label>Jot Form File (doc,pdf,docx)</Label>
                      <Input
                        type="file"
                        name="jotFormFile"
                        accept=".doc,.pdf,.docx"
                        onChange={handleInputChange}
                      />
                      {showFieldError("jotFormFile") && (
                        <small className="text-danger">{errors.jotFormFile}</small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>MEC Lab Location</Label>
                      <Input
                        name="mecLabLocation"
                        value={form.mecLabLocation}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {showFieldError("mecLabLocation") && (
                        <small className="text-danger">{errors.mecLabLocation}</small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <h5 className="mt-3 mb-3">FADV Details</h5>
                <hr />
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label>FADV Login</Label>
                      <Select
                        options={fadvLoginOptions}
                        className="select2-selection"
                        placeholder="FADV Login"
                        value={getSelectedOption(fadvLoginOptions, form.fadvLogin)}
                        onChange={(option) => handleSelectChange("fadvLogin", option)}
                        isDisabled={isViewMode}
                      />
                      {showFieldError("fadvLogin") && <small className="text-danger">{errors.fadvLogin}</small>}
                    </FormGroup>
                  </Col>
                  {showFadvDetails ? (
                    <>
                      <Col md={4}>
                        <FormGroup>
                          <Label>
                            FADV Account
                            <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            name="fadvAccount"
                            value={form.fadvAccount}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          />
                          {showFieldError("fadvAccount") && <small className="text-danger">{errors.fadvAccount}</small>}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>
                            FADV User
                            <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            name="fadvUser"
                            value={form.fadvUser}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          />
                          {showFieldError("fadvUser") && <small className="text-danger">{errors.fadvUser}</small>}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>
                            FADV Password
                            <span className="text-danger"> *</span>
                          </Label>
                          <Input
                            name="fadvPassword"
                            value={form.fadvPassword}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                          />
                          {showFieldError("fadvPassword") && <small className="text-danger">{errors.fadvPassword}</small>}
                        </FormGroup>
                      </Col>
                    </>
                  ) : null}
                </Row>
                <h5 className="mt-3 mb-3">Note</h5>
                <hr />
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Note</Label>
                      <Input
                        type="textarea"
                        rows="3"
                        name="onboardingNote"
                        value={form.onboardingNote}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {showFieldError("onboardingNote") && <small className="text-danger">{errors.onboardingNote}</small>}
                    </FormGroup>
                  </Col>
                </Row>
                {showApproveCheckbox ? (
                  <Row className="mt-2">
                    <Col md={12}>
                      <div className="border rounded bg-white px-3 py-2 bg-cyan-check">
                        <FormGroup check className="mb-0 d-flex align-items-center gap-2">
                          <Input
                            type="checkbox"
                            id="onboardingApproveCheckbox"
                            name="isApproved"
                            key={`onboarding-isApproved-${String(form.isApproved)}-${editId || "new"}`}
                            defaultChecked={Boolean(form.isApproved)}
                            onChange={handleInputChange}
                            className="mt-0"
                          />
                          <Label
                            check
                            htmlFor="onboardingApproveCheckbox"
                            className="mb-0"
                            style={{ color: "#E67E61" }}
                          >
                            Please check this box if you want to Approve.
                          </Label>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                ) : null}
                {!isViewMode ? (
                <div className="text-end">
                  <Button color="primary" className="mt-3 text-end" disabled={saving}>
                    {saving ? "Saving…" : isEditMode ? "Update" : "Add"}
                  </Button>
                </div>
                ) : null}
                </fieldset>

              </Form>
              )}
            </CardBody>
          </Card>
          <CompanyAddEditModal
            setCompanyAddEditModalOpen={setCompanyAddEditModalOpen}
            companyAddEditModalOpen={companyAddEditModalOpen}
            getCompanyList={handleCompanyListRefresh}
            itemId={null}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddEditOnboarding;
